import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Skeleton } from "../../components/ui/skeleton";
import ExpenseCard from "../../components/expenses/ExpenseCard";
import StatCard from "../../components/dashboard/StatCard";
import StickyHeading from "../../components/common/StickyHeading";
import {
  Sparkles,
  Wallet,
  CheckCircle2,
  CircleDollarSign,
  Trophy,
  Inbox,
  X,
  XCircle,
  Loader2,
} from "lucide-react";
import {
  STATUSES,
  formatINR,
} from "../../lib/expenseOptions";
import { joinParts, num } from "../../lib/vocabulary";
import { toast } from "sonner";

const ALL = "__all__";

export default function AllExpensesPage() {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sos, setSos] = useState([]);
  // From the backend's own list (`active_expense_types` via `/expenses/
  // meta`), never a frontend-owned array — a hardcoded, stale copy of this
  // is what made every expense submission fail "Unknown expense_type".
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  // Tracked separately from the list: the summary has its own fetch, and
  // "still loading" must not read as "₹0 claimed this month".
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [error, setError] = useState("");
  const [soId, setSoId] = useState(ALL);
  const [type, setType] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [actionMode, setActionMode] = useState(null);
  const [remark, setRemark] = useState("");
  const [acting, setActing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: u }, { data: s }, { data: m }] = await Promise.all([
          api.get("/users"),
          api.get("/expenses/summary"),
          api.get("/expenses/meta"),
        ]);
        setSos(u.filter((x) => x.role === "sales_officer" && x.is_active));
        setSummary(s);
        setExpenseTypes(m.expense_types || []);
      } catch (_) {
        // Swallowed on purpose (the list below is the page's real content),
        // which is exactly why `summary` must stay null and the tiles must
        // show `—` rather than a total nobody computed.
      } finally {
        setSummaryLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const params = {};
    if (soId !== ALL) params.so_id = soId;
    if (type !== ALL) params.expense_type = type;
    if (status !== ALL) params.status = status;
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    let cancelled = false;
    setLoading(true);
    api
      .get("/expenses", { params })
      .then(({ data }) => !cancelled && setExpenses(data))
      .catch((e) => !cancelled && setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [soId, type, status, from, to]);

  // A SUMMARY THAT NEVER ARRIVED IS NOT A TOTAL OF ZERO (Pattern A / RC-11).
  // `summary?.by_status || {}` turned a failed fetch into "Claimed ₹0 across
  // all statuses" on the company-wide page — and the fetch above swallows its
  // error, so nothing else on the page would have said otherwise. A `by_status`
  // that arrived without a given status IS a real zero; the server emits only
  // the statuses that have rows.
  const totals = useMemo(() => {
    const s = summary?.by_status;
    if (!s) return { claimed: null, approved: null, pending: null };
    return {
      claimed:
        (s.Submitted?.amount || 0) + (s.Approved?.amount || 0) +
        (s.Rejected?.amount || 0) + (s.Draft?.amount || 0),
      approved: s.Approved?.amount || 0,
      pending: s.Submitted?.amount || 0,
    };
  }, [summary]);
  const money = (v) => num(summaryLoading, v === null ? null : formatINR(v));
  const topSpenders = summary?.top_spenders || [];
  const byType = summary?.by_type || [];

  const hasFilters = soId !== ALL || type !== ALL || status !== ALL || !!from || !!to;
  const clear = () => {
    setSoId(ALL);
    setType(ALL);
    setStatus(ALL);
    setFrom("");
    setTo("");
  };

  // NO ROLE CLAUSE. This used to be `role === "admin" || role === "director"`,
  // a second copy of the approval rule in a file that must not hold one — and
  // it said something that was never true of the Tech Admin's authority and is
  // now plainly false: he is in no chain and approves nothing. `can_approve`
  // comes from the backend's `can_approve_submission`, which is the rule, so
  // the button follows it and only it.

  async function submitAction() {
    if (!actionMode) return;
    if (actionMode.kind === "reject" && !remark.trim()) {
      toast.error("Please add a reason for rejection.");
      return;
    }
    setActing(true);
    try {
      await api.put(`/expenses/${actionMode.expense.id}/${actionMode.kind}`, {
        remark: remark.trim() || null,
      });
      toast.success(actionMode.kind === "approve" ? "Approved" : "Rejected");
      setActionMode(null);
      setRemark("");
      const newExpenses = expenses.map((e) =>
        e.id === actionMode.expense.id ? { ...e, status: actionMode.kind === "approve" ? "Approved" : "Rejected" } : e
      );
      setExpenses(newExpenses);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setActing(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="all-expenses-page">
      <StickyHeading
        icon={Sparkles}
        label="Company Expense Log"
        title="All expenses"
        subtitle="Every field expense, this month at a glance."
        testId="all-expenses-heading"
      />

      {/* Headline stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard testId="all-expenses-stat-claimed" label="Claimed (month)" value={money(totals.claimed)} helper="Across all statuses" Icon={Wallet} loading={summaryLoading} />
        <StatCard testId="all-expenses-stat-pending" label="Pending" value={money(totals.pending)} helper="Submitted, not decided" Icon={CircleDollarSign} loading={summaryLoading} />
        <StatCard testId="all-expenses-stat-approved" label="Approved" value={money(totals.approved)} helper="Reimbursable" Icon={CheckCircle2} accent loading={summaryLoading} />
      </div>

      {/* Top spenders + by type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-white border border-callisto rounded-xl p-4">
          <p className="text-base font-semibold text-callisto-navy flex items-center gap-2">
            <Trophy className="h-4 w-4 text-callisto-gold" />
            Top spenders · This month
          </p>
          {topSpenders.length === 0 ? (
            <p className="mt-2 text-sm text-callisto-muted">No approved expenses yet.</p>
          ) : (
            <ol className="mt-3 space-y-1.5" data-testid="all-expenses-top">
              {topSpenders.map((s, idx) => (
                <li key={s.id} className="flex items-center gap-2 rounded-md px-2.5 py-2 bg-callisto-app">
                  <span className="h-6 w-6 shrink-0 rounded-full bg-callisto-navy text-white text-[11px] font-bold flex items-center justify-center">
                    {idx + 1}
                  </span>
                  <span className="text-sm font-semibold text-callisto-navy flex-1 truncate">{s.name}</span>
                  <span className="text-sm font-bold text-callisto-navy">{formatINR(s.amount)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
        <div className="bg-white border border-callisto rounded-xl p-4">
          <p className="text-base font-semibold text-callisto-navy">By type · This month</p>
          {byType.length === 0 ? (
            <p className="mt-2 text-sm text-callisto-muted">No data yet.</p>
          ) : (
            <ul className="mt-3 space-y-1.5" data-testid="all-expenses-by-type">
              {byType.map((b) => (
                <li key={b.type} className="flex items-center justify-between text-sm px-2.5 py-1.5">
                  <span className="text-callisto-body">{b.type}</span>
                  <span className="font-bold text-callisto-navy">{formatINR(b.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-callisto rounded-xl p-3 md:p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Select value={soId} onValueChange={setSoId}>
            <SelectTrigger className="h-11 col-span-2 md:col-span-1 focus:ring-callisto-gold" data-testid="all-expenses-filter-so">
              <SelectValue placeholder="Sales officer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All officers</SelectItem>
              {sos.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="all-expenses-filter-type">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All types</SelectItem>
              {expenseTypes.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="all-expenses-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            max={to || undefined}
            className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="all-expenses-filter-from"
          />
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            min={from || undefined}
            className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="all-expenses-filter-to"
          />
        </div>
        {hasFilters && (
          <Button
            variant="ghost"
            onClick={clear}
            className="mt-2 h-9 text-callisto-muted hover:text-callisto-navy"
            data-testid="all-expenses-filters-clear"
          >
            <X className="h-3 w-3 mr-1" /> Clear filters
          </Button>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
          {error}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
      ) : expenses.length === 0 ? (
        <div className="bg-white border border-callisto rounded-xl p-10 flex flex-col items-center text-center">
          <div className="h-14 w-14 rounded-xl bg-callisto-navy/5 flex items-center justify-center mb-3">
            <Inbox className="h-6 w-6 text-callisto-navy" />
          </div>
          <p className="text-sm font-semibold text-callisto-navy">No expenses match</p>
          <p className="mt-1 text-xs text-callisto-muted">Try clearing filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="all-expenses-grid">
          {expenses.map((e) => (
            <ExpenseCard
              key={e.id}
              expense={e}
              showOfficer
              action={
                e.can_approve && e.status === "Submitted" ? (
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      onClick={() => setActionMode({ expense: e, kind: "approve" })}
                      className="h-7 px-2.5 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`all-expenses-approve-${e.id}`}
                    >
                      <CheckCircle2 size={12} className="mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setActionMode({ expense: e, kind: "reject" })}
                      className="h-7 px-2.5 text-[11px] border-red-200 text-red-700 hover:bg-red-50"
                      data-testid={`all-expenses-reject-${e.id}`}
                    >
                      <XCircle size={12} className="mr-1" />
                      Reject
                    </Button>
                  </div>
                ) : undefined
              }
            />
          ))}
        </div>
      )}

      <Dialog
        open={!!actionMode}
        onOpenChange={(o) => {
          if (!o) {
            setActionMode(null);
            setRemark("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionMode?.kind === "approve" ? "Approve expense" : "Reject expense"}
            </DialogTitle>
            <DialogDescription>
              {joinParts([
                actionMode?.expense?.sales_officer_name,
                actionMode?.expense?.expense_type,
                actionMode?.expense && formatINR(actionMode.expense.amount),
              ])}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            rows={3}
            placeholder={actionMode?.kind === "approve" ? "Optional remark" : "Reason for rejection (required)"}
            className="focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="all-expenses-remark"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setActionMode(null);
                setRemark("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={submitAction}
              disabled={acting}
              className={
                actionMode?.kind === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"
              }
              data-testid="all-expenses-action-confirm"
            >
              {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : actionMode?.kind === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
